import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableFooter,
    LinearProgress,
    Pagination,
    IconButton,
    Icon,
    Typography,
    Box,
    Button,
    TextField
}
    from '@mui/material'

import { IUser } from '../../shared/protocolos';
import { useDebounce } from '../../shared/hooks';
import { ExportButtons, Toolbar } from '../../shared/components';
import { Environment } from '../../shared/environments';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { UserService } from '../../shared/services/user/userService';
import { SettingsService } from '../../shared/services/settings/settingsService';
import { formatCPF, formatPhoneNumber, formatCEP } from 'shared/utils';

const formatSupportWhatsapp = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 13);
    const countryCode = digits.slice(0, 2);
    const areaCode = digits.slice(2, 4);
    const phone = digits.slice(4);

    if (!countryCode) return '';

    let formatted = `+${countryCode}`;
    if (areaCode) formatted += ` (${areaCode}`;
    if (areaCode.length === 2) formatted += ')';

    if (phone) {
        const firstPartLength = phone.length > 8 ? 5 : 4;
        const firstPart = phone.slice(0, firstPartLength);
        const secondPart = phone.slice(firstPartLength, firstPartLength + 4);
        formatted += ` ${firstPart}`;
        if (secondPart) formatted += `-${secondPart}`;
    }

    return formatted;
};


export const User: React.FC = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const { debounce } = useDebounce();
    const navigate = useNavigate();


    const [rows, setRows] = useState<IUser[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [supportWhatsapp, setSupportWhatsapp] = useState('')
	const [supportEmail, setSupportEmail] = useState('')
	const [savingSupport, setSavingSupport] = useState(false)

    const busca = useMemo(() => {
        return searchParams.get('busca') || '';
    }, [searchParams]);

    const pagina = useMemo(() => {
        return Number(searchParams.get('pagina') || "1");
    }, [searchParams]);

    useEffect(() => {
        setIsLoading(true);
        debounce(async () => {
            handleTableList();
        });
    }, [busca, pagina]);

    useEffect(() => {
        SettingsService.getSupportConfig().then(result => {
			if (!(result instanceof Error)) {
				setSupportWhatsapp(formatSupportWhatsapp(result.whatsapp));
				setSupportEmail(result.email || '');
			}
        });
    }, []);

    const handleTableList = async () => {
        await UserService.listAll("id", pagina, "asc", busca)
            .then((result) => {
                setIsLoading(false);

                if (result instanceof Error) {
                    alert(result.message);
                } else {
                    setTotalCount(result.totalCount);
                    setRows(result.data);
                }
            });
    }

    const handleDelete = async (id: string) => {
        if (window.confirm('Esta exclusão é definitiva e não poderá ser desfeita. Deseja continuar?')) {
            await UserService.deleteById(id)
                .then(result => {
                    if (result instanceof Error) {
                        alert(result.message);
                    } else {
                        handleTableList();
                        alert('Registro apagado com sucesso!');
                    }
                });
        }
    };

    const handleDisable = async (id: string) => {
        if (!window.confirm('Deseja desabilitar temporariamente este usuário?')) return;

        const result = await UserService.disableById(id);
        if (result instanceof Error) return alert(result.message);
        alert('Usuário desabilitado com sucesso!');
        handleTableList();
    };

    const handleReactivate = async (id: string) => {
        if (!window.confirm('Deseja reativar este usuário?')) return;

        const result = await UserService.reactivateById(id);
        if (result instanceof Error) return alert(result.message);
        alert('Usuário reativado com sucesso!');
        handleTableList();
    };

    const handleResendConfirmation = async (id: string) => {
        if (!window.confirm('Reenviar o e-mail de confirmacao para este usuario?')) return;
        const result = await UserService.resendConfirmationEmailByAdmin(id);
        alert(result.message);
    };

	const handleSaveSupport = async () => {
		setSavingSupport(true);
		const result = await SettingsService.updateSupportConfig(supportWhatsapp, supportEmail);
		setSavingSupport(false);
		if (result instanceof Error) return alert(result.message);
		setSupportWhatsapp(formatSupportWhatsapp(result.whatsapp));
		setSupportEmail(result.email);
		alert(result.message);
    };

    return (
        <LayoutBaseDePagina
            barraDeFerramentas={
                <Toolbar
                    mostrarInputBusca
                    textoDaBusca={busca}
                    textoBotaoNovo='Nova'
                    acoesExtras={<ExportButtons title="Usuários" fileName="usuarios" rows={rows} totalCount={totalCount} pageSize={Environment.LIMIT}
                        loadPage={async exportPage => { const result = await UserService.listAll('id', exportPage, 'asc', busca); return result instanceof Error ? result : result.data; }} disabled={isLoading} columns={[
                        { label: 'Nome', value: row => row.name }, { label: 'E-mail', value: row => row.email },
                        { label: 'CPF', value: row => formatCPF(row.cpf) }, { label: 'Telefone', value: row => formatPhoneNumber(row.telefone || '') },
                        { label: 'Status', value: row => row.status }, { label: 'Tipo de Usuário', value: row => row.role },
                        { label: 'Endereço', value: row => row.endereco }, { label: 'Bairro', value: row => row.bairro },
                        { label: 'CEP', value: row => formatCEP(row.cep || '') }, { label: 'Cidade', value: row => row.cidade },
                        { label: 'UF', value: row => row.uf },
                    ]} />}
                    aoClicarEmNovo={() => { navigate('/user/detalhe/nova') }}
                    aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, pagina: '1' }, { replace: true })}
                />
            }
        >
            <Paper variant='outlined' sx={{ m: 1, p: 2 }}>
				<Typography variant='h6' gutterBottom>Canais de atendimento</Typography>
				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					WhatsApp e e-mail exibidos no aplicativo para usuários que precisem de suporte.
				</Typography>
				<Box sx={{ display: 'flex', gap: 2, alignItems: 'center', maxWidth: 900, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <TextField
                        fullWidth
                        label='WhatsApp com código do país e DDD'
                        value={supportWhatsapp}
                        onChange={event => setSupportWhatsapp(formatSupportWhatsapp(event.target.value))}
                        placeholder='+55 (85) 99999-9999'
						inputProps={{ inputMode: 'tel', maxLength: 20 }}
					/>
					<TextField
						fullWidth
						type='email'
						label='E-mail de suporte'
						value={supportEmail}
						onChange={event => setSupportEmail(event.target.value.toLowerCase().replace(/\s/g, ''))}
						placeholder='suporte@qrmania.com.br'
					/>
					<Button variant='contained' onClick={handleSaveSupport} disabled={savingSupport} sx={{ minWidth: 100 }}>
						Salvar
                    </Button>
                </Box>
            </Paper>
            <TableContainer component={Paper} variant='outlined' sx={{ m: 1, width: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width={100}>Ações</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Tipo de Usuário</TableCell>
                            <TableCell>Endereço</TableCell>
                            <TableCell>Bairro</TableCell>
                            <TableCell>CEP</TableCell>
                            <TableCell>Cidade</TableCell>
                            <TableCell>UF</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            // whiteSpace="nowrap" textOverflow="ellipsis"
                            <TableRow key={row.id}>
                                <TableCell>
                                    {row.deletedAt || row.status === 'INATIVO' ? (
                                        <IconButton title="Reativar usuário" color="success" onClick={() => handleReactivate(row.id as string)}>
                                            <Icon>restore</Icon>
                                        </IconButton>
                                    ) : (
                                        <IconButton title="Desabilitar temporariamente" color="warning" onClick={() => handleDisable(row.id as string)}>
                                            <Icon>block</Icon>
                                        </IconButton>
                                    )}
                                    {row.role !== 'ADMIN' && row.role !== 'SUPER_ADMIN' && (
                                        <IconButton title="Excluir permanentemente" color="error" onClick={() => handleDelete(row.id as string)}>
                                            <Icon>delete_forever</Icon>
                                        </IconButton>
                                    )}
                                    {!row.deletedAt && row.status !== 'INATIVO' && (
                                        <IconButton title="Editar usuário" onClick={() => navigate(`/user/detalhe/${row.id}`)}>
                                            <Icon>edit</Icon>
                                        </IconButton>
                                    )}
                                    {!row.emailVerifiedAt && !row.deletedAt && (
                                        <IconButton title='Reenviar confirmacao de e-mail' onClick={() => handleResendConfirmation(row.id as string)}>
                                            <Icon>mark_email_unread</Icon>
                                        </IconButton>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" variant='inherit'>
                                        {row.name}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatCPF(row.cpf)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatPhoneNumber(row.telefone || "")}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.status}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.role}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.endereco}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.bairro}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatCEP(row.cep || "")}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.cidade}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.uf}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    {totalCount === 0 && !isLoading &&
                        (
                            <caption>{Environment.LISTAGEM_VAZIA}</caption>
                        )
                    }
                    <TableFooter>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <LinearProgress variant='indeterminate' />
                                </TableCell>
                            </TableRow>
                        )}
                        {(totalCount > 0 && totalCount > Environment.LIMIT) && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Pagination
                                        page={pagina}
                                        count={Math.ceil(totalCount / Environment.LIMIT)}
                                        onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableFooter>
                </Table>
            </TableContainer>
        </LayoutBaseDePagina>
    );
};
